// Journey-Stage Adaptive Dashboards — portal-native dashboard primitives.
//
// These mirror the STAFF adaptive engine's presentational layer (the
// `WidgetFrame` + responsive grid + stat/queue renderers in `@/dashboard/*`)
// WITHOUT importing it — portal isolation (`portal/isolation.test.ts`)
// forbids the `@/dashboard` prefix outright. Everything here depends only on
// the shared design system, so it is portal-safe.
//
// This file is the PRESENTATIONAL layer only. The composition machinery —
// recipe, widget catalog, per-widget `WidgetFrame` with independent
// load/error/retry, and the page layout — lives in `./engine.tsx`; every
// portal Home renders through that engine and uses these pieces inside it.
//
// Mobile: `DashboardGrid` and `StatTileRow` both stack to a single column
// below `md` so first paint on a phone is one readable column.
//
// Widths: `StatTileRow` lays out on the VIEWPORT by default and clips anything
// that does not fit. That is correct for a row spanning the page and wrong for
// one nested in a `DashboardGrid` card, where the two viewport grids fight and
// the tiles are squeezed hardest on the WIDEST screens — so a narrow card
// passes `fluid` for a container-driven row whose tiles wrap instead. See
// `FLUID_TILE_MIN_WIDTH` for the derivation.

import { createContext, useContext, type ReactNode } from "react";
import {
  Card,
  ResponsiveGrid,
  Skeleton,
  StatusStepper,
  type StepperStep,
} from "@/design-system/components/ui";
import { formatProgressPercent } from "@/design-system/lib/format";
import { cn } from "@/design-system/lib/utils";

export type WidgetTone = "default" | "success" | "warning" | "danger";

const TONE_TEXT: Record<WidgetTone, string> = {
  default: "text-onyx",
  success: "text-status-success",
  warning: "text-status-warning",
  danger: "text-status-error",
};

// ── Layout ───────────────────────────────────────────────────────────────────

/** Responsive dashboard grid. One column on phones, up to three on desktop. */
export function DashboardGrid({
  children,
  className,
  "data-testid": testId,
}: {
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
}) {
  return (
    <div
      data-testid={testId}
      className={cn(
        "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-space-4 items-start",
        className,
      )}
    >
      {children}
    </div>
  );
}

function spanClass(span?: "full" | "wide"): string {
  if (span === "full") return "md:col-span-2 xl:col-span-3";
  if (span === "wide") return "xl:col-span-2";
  return "";
}

/** A framed dashboard widget: optional title + action, a loading skeleton,
 *  and its body. Callers pass an <EmptyState> (or muted copy) as children
 *  when there is nothing to show — never a fake zero. */
export function DashboardCard({
  title,
  action,
  loading,
  span,
  className,
  children,
  testId,
}: {
  title?: string;
  action?: ReactNode;
  loading?: boolean;
  span?: "full" | "wide";
  className?: string;
  children?: ReactNode;
  testId?: string;
}) {
  return (
    <Card className={cn("space-y-space-3", spanClass(span), className)} data-testid={testId}>
      {(title || action) && (
        <div className="flex items-center justify-between gap-space-3">
          {title && (
            <h3 className="text-small font-semibold uppercase tracking-wide text-gray-mid m-0">
              {title}
            </h3>
          )}
          {action}
        </div>
      )}
      {loading ? <Skeleton className="h-24 w-full" /> : children}
    </Card>
  );
}

// ── Stat tiles ───────────────────────────────────────────────────────────────

/**
 * 🔴 THE NARROWEST A FLUID TILE MAY GET BEFORE THE ROW SHEDS A COLUMN — the
 * one number here that had to be derived rather than picked, per the
 * `StatsBar` precedent that converted the staff counter strip the same way.
 *
 * The tightest container these rows live in is a `DashboardGrid` card once the
 * outer grid reaches three columns. At a 1280px viewport behind the 256px
 * sidebar: content `1280 − 256 − 2·48 = 928px`; card `(928 − 2·24)/3 = 293px`;
 * card inner `293 − 2·24 = 245px`. A tile spends `2·16px` on its own padding,
 * so its text track is `min − 32`.
 *
 * THE LABEL BINDS, NOT THE VALUE — which is the opposite of what a money tile
 * suggests. `Under contract` / `Need agreement` set uppercase with
 * `tracking-wide` need ≈139px at the size these actually render, while the
 * widest un-abbreviated value `$9,999.99` needs ≈86px. `139 + 32 = 171px`, so
 * 11rem (176px) is the next round value that clears it.
 *
 * Where each count sheds a column, at `N·176 + (N-1)·16`:
 * 3 → 560px · 2 → 368px. Inside a 245px card that is one column, which is the
 * honest answer for a 14-character uppercase label — and the row widens itself
 * to two or three the moment a container can afford it.
 */
const FLUID_TILE_MIN_WIDTH = "11rem";

/** Set by a `fluid` row so its tiles stop clipping. Read only by `StatTile`,
 *  so a fluid row can never contain a truncating tile by omission. */
const FluidTileContext = createContext(false);

/**
 * Row of compact KPI tiles. One per row on phones (readable, truncation-
 * safe), four on desktop.
 *
 * `fluid` opts into a CONTAINER-driven row instead. The default lays tiles out
 * on the VIEWPORT (`md:grid-cols-4`), which is correct only for a row that
 * spans the page: nested inside a `DashboardGrid` card the outer grid also
 * reflows on the viewport, so past `xl` the card narrows to a third of the
 * page while the row stubbornly holds four columns and the tiles are squeezed
 * hardest on the LARGEST screens. A fluid row reflows on the box it is
 * actually in, and its tiles wrap rather than clip.
 *
 * The default path is untouched, so every row that has not opted in renders
 * exactly the DOM it always did.
 */
export function StatTileRow({
  children,
  className,
  fluid = false,
}: {
  children: ReactNode;
  className?: string;
  fluid?: boolean;
}) {
  if (!fluid) {
    return (
      <div className={cn("grid grid-cols-1 md:grid-cols-4 gap-space-3", className)}>
        {children}
      </div>
    );
  }
  return (
    <FluidTileContext.Provider value={true}>
      <ResponsiveGrid
        cols={3}
        minColWidth={FLUID_TILE_MIN_WIDTH}
        gap="sm"
        className={className}
      >
        {children}
      </ResponsiveGrid>
    </FluidTileContext.Provider>
  );
}

export function StatTile({
  label,
  value,
  hint,
  tone = "default",
  onClick,
  testId,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: WidgetTone;
  /** When set the whole tile is a drill-in button (the staff StatCard-as-
   *  button precedent) — use it to deep-link the tile's own tab/detail. */
  onClick?: () => void;
  testId?: string;
}) {
  // `truncate` is `overflow:hidden` + `text-overflow:ellipsis` + `nowrap`, and
  // it is the literal mechanism that rendered "Outstanding" as "O…" and
  // "$1,000.00" as "$…" once a tile got down to ~17px of text track. Inside a
  // `fluid` row the tile is never narrower than it can read at, so the label
  // and value wrap instead — clipping a value is never the right answer, and a
  // `title` tooltip revealing it is a workaround for the same bug.
  const clip = !useContext(FluidTileContext);
  const body = (
    <>
      <p
        className={cn(
          "text-caption uppercase tracking-wide text-gray-mid m-0",
          clip && "truncate",
        )}
      >
        {label}
      </p>
      <p
        className={cn(
          "text-h3 font-semibold m-0",
          // `break-words` makes the clipped-money defect structurally
          // impossible rather than merely unlikely: it holds even if a caller
          // hands this an un-abbreviated figure the column cannot seat.
          clip ? "truncate" : "break-words",
          TONE_TEXT[tone],
        )}
        title={clip && typeof value === "string" ? value : undefined}
      >
        {value}
      </p>
      {hint && <p className="text-caption text-gray-mid m-0">{hint}</p>}
    </>
  );
  if (onClick) {
    return (
      <Card className="p-0 min-w-0" data-testid={testId}>
        <button
          type="button"
          onClick={onClick}
          className="block w-full text-left p-space-3 space-y-space-1 min-w-0 rounded-lg hover:bg-white-warm"
        >
          {body}
        </button>
      </Card>
    );
  }
  return (
    <Card className="p-space-3 space-y-space-1 min-w-0" data-testid={testId}>
      {body}
    </Card>
  );
}

// ── Progress bar ─────────────────────────────────────────────────────────────

/** Shared 0..1 progress bar (payoff progress, onboarding-course progress).
 *  The ONE portal progress bar — never hand-roll another (audit F-40 found
 *  two bespoke ones with mismatched radius tokens). */
export function ProgressBar({
  fraction,
  label,
  testId,
}: {
  /** 0..1; clamped. */
  fraction: number;
  label?: string;
  testId?: string;
}) {
  const pct = Math.min(100, Math.max(0, Math.round(fraction * 100)));
  return (
    <div className="space-y-space-1" data-testid={testId}>
      {label && (
        <div className="flex items-center justify-between gap-space-2">
          <p className="text-caption text-gray-mid m-0">{label}</p>
          {/* One decimal below 1% so a real payoff step never reads "0%". */}
          <p className="text-caption font-semibold m-0">{formatProgressPercent(fraction)}</p>
        </div>
      )}
      <div
        className="h-2 rounded-pill bg-white-warm overflow-hidden"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        {/* Dynamic width is data, not a design constant — inline style is correct here. */}
        <div className="h-full rounded-pill bg-gold" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ── Next best action ─────────────────────────────────────────────────────────

/** The single "what to do next" card. It must only ever surface an action
 *  that is available RIGHT NOW for the user's stage — never a checklist item
 *  the rest of the screen says is unavailable. When there is nothing to do,
 *  pass an all-clear `heading` and no `actions`. */
export function NextBestActionCard({
  heading,
  description,
  actions,
  tone = "default",
  testId,
}: {
  heading: string;
  description?: string;
  actions?: ReactNode;
  tone?: WidgetTone;
  testId?: string;
}) {
  return (
    <Card featured className="space-y-space-2 py-space-5" data-testid={testId}>
      <p className="text-caption uppercase tracking-wide text-gold-shadow m-0">Next step</p>
      <p className={cn("text-h3 font-semibold m-0", TONE_TEXT[tone])}>{heading}</p>
      {description && <p className="text-small text-gray-mid m-0">{description}</p>}
      {actions && <div className="flex flex-wrap items-center gap-space-2 pt-space-2">{actions}</div>}
    </Card>
  );
}

// ── Grade badge ──────────────────────────────────────────────────────────────

const GRADE_CLASS: Record<string, string> = {
  A: "bg-status-success text-white-pure",
  B: "bg-status-success/80 text-white-pure",
  C: "bg-gold text-onyx",
  D: "bg-status-warning text-white-pure",
  F: "bg-status-error text-white-pure",
};

/** A compact A–F grade chip. Presentational only — the caller decides what the
 *  grade means (e.g. a coaching self-signal). Falls back to a neutral onyx
 *  chip for any unknown letter. */
export function GradeBadge({
  grade,
  hint,
  className,
}: {
  grade: string;
  hint?: string;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-7 min-w-7 items-center justify-center rounded-pill px-space-2",
        "font-display text-small font-bold",
        GRADE_CLASS[grade] ?? "bg-onyx text-white-pure",
        className,
      )}
      title={hint}
      aria-label={hint ? `Grade ${grade}: ${hint}` : `Grade ${grade}`}
    >
      {grade}
    </span>
  );
}

// ── Journey spine ────────────────────────────────────────────────────────────

/** The horizontal "where you are in your journey" tracker. Thin wrapper over
 *  the shared StatusStepper so every portal Home frames its lifecycle the
 *  same way. */
export function JourneySpine({
  steps,
  currentIndex,
  complete,
  className,
  testId,
}: {
  steps: StepperStep[];
  currentIndex: number;
  complete?: boolean;
  className?: string;
  testId?: string;
}) {
  return (
    <div className={cn("space-y-space-2", className)}>
      <p className="text-caption uppercase tracking-wide text-gray-mid m-0">Your journey</p>
      <StatusStepper
        steps={steps}
        currentIndex={currentIndex}
        complete={complete}
        label="Your journey"
        data-testid={testId}
      />
    </div>
  );
}
